import React, { useCallback } from 'react';
import {
    FormGroup,
    FormGroupProps,
    Flex,
    FlexProps,
    HStack,
    HStackProps,
    TextProps,
    Icon,
    IconNameT,
    StackProps,
    Text,
    DataItem,
} from '@cad-arc';
import { FormEditable } from '../../forms/integrated-form-components/form-editable';
import {
    useEventNotification,
    useEventNotifications,
    useRemoveEventNotifications,
} from '../../event-notifications/use-event-notifications';
import { EventNotificationsFieldIndicator } from '../../event-notifications/event-notifications-field-indicator';
import { CadNextNotificationTypeEnumType } from '@mark43/cad-api';
import { eventNotificationPriorityToColors } from '../../event-notifications/event-notifications';
import { observer } from 'mobx-react-lite';
import { useWorkspacePanel } from '../hooks/use-workspace-panel';

export const NARRATIVE_PANEL_WITH_TWO_INFOS_HEIGHT = 143;

type EntityPanelFormGroupProps = FormGroupProps &
    React.HTMLAttributes<HTMLDivElement> & {
        children: React.ReactNode;
    };
export const EntityPanelFormGroup = ({
    children,
    style,
    ...props
}: EntityPanelFormGroupProps): React.ReactElement => {
    return (
        <FormGroup spacing={2} style={{ width: '100%', display: 'block', ...style }} {...props}>
            {children}
        </FormGroup>
    );
};

type EntityPanelGroupLabelProps = TextProps;

export const EntityPanelGroupLabel = ({
    children,
    ...props
}: EntityPanelGroupLabelProps): React.ReactElement => {
    return (
        <Text variant="headingSm" {...props}>
            {children}
        </Text>
    );
};

type EntityPanelGroupHeaderProps = HStackProps & { icon?: IconNameT; title?: string };

export const EntityPanelGroupHeader = ({
    children,
    icon,
    title,
    ...props
}: EntityPanelGroupHeaderProps): React.ReactElement => {
    return (
        <HStack {...props}>
            {children ?? (
                <>
                    {icon && <Icon icon={icon} />}
                    <EntityPanelGroupLabel>{title}</EntityPanelGroupLabel>
                </>
            )}
        </HStack>
    );
};

// Agency field (Police / Fire / EMS) components
type AgencyFieldsDisplayRowProps = FlexProps;

export const AgencyFieldsDisplayRow = ({
    children,
    style,
    ...props
}: AgencyFieldsDisplayRowProps): React.ReactElement => {
    return (
        <Flex
            style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--arc-space-3)', ...style }}
            {...props}
        >
            {children}
        </Flex>
    );
};

type AgencyFormGroupWithHeaderProps = StackProps & {
    agency: 'EMS' | 'Police' | 'Fire';
    hasNotifications?: boolean;
    showHeader: boolean;
};
export function AgencyFormGroupWithHeader({
    children,
    agency,
    onBlur,
    hasNotifications,
    showHeader,
}: AgencyFormGroupWithHeaderProps): React.ReactElement {
    return (
        <EntityPanelFormGroup spacing={1} onBlur={onBlur}>
            {showHeader && (
                <EntityPanelGroupHeader>
                    <Icon
                        size="sm"
                        icon={agency}
                        color={
                            hasNotifications
                                ? eventNotificationPriorityToColors['STANDARD'].notchOrText
                                : undefined
                        }
                    />
                    <EntityPanelGroupLabel
                        style={{
                            lineHeight: '1.5rem',
                            color: hasNotifications
                                ? eventNotificationPriorityToColors['STANDARD'].notchOrText
                                : undefined,
                        }}
                    >
                        {agency}
                    </EntityPanelGroupLabel>
                </EntityPanelGroupHeader>
            )}
            {children}
        </EntityPanelFormGroup>
    );
}

type AgencyFormGroupEditableProps = {
    toggleMode?: () => void;
    agency: AgencyFormGroupWithHeaderProps['agency'];
    viewMode: React.ReactElement;
    editMode: React.ReactElement;
};

export const DataItemWithEventNotification = observer(function DataItemWithEventNotification(
    props: React.ComponentProps<typeof DataItem> & {
        eventNotificationName?: CadNextNotificationTypeEnumType;
    }
) {
    const { eventNotificationName, children, ...dataItemProps } = props;
    const eventNotification = useEventNotification({ notification: props.eventNotificationName });
    const removeEventNotifications = useRemoveEventNotifications();
    return (
        <DataItem
            {...dataItemProps}
            style={{
                fontSize: 'var(--arc-fontSizes-sm)',
                color: eventNotification
                    ? eventNotificationPriorityToColors[eventNotification.notificationPriority]
                          .notchOrText
                    : undefined,
            }}
            onClick={
                eventNotification
                    ? () => {
                          removeEventNotifications({
                              notifications: [eventNotification.notificationType],
                          });
                      }
                    : undefined
            }
        >
            <Text fontWeight="semibold">{children}</Text>
        </DataItem>
    );
});

export const AgencyFormGroupEditable = observer(function AgencyFormGroupEditable({
    agency,
    viewMode,
    editMode,
}: AgencyFormGroupEditableProps): React.ReactElement {
    function handleBlur(
        event: React.FocusEvent<HTMLSpanElement>,
        toggleMode: AgencyFormGroupEditableProps['toggleMode']
    ) {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            toggleMode && toggleMode();
        }
    }
    const eventNotifications = useEventNotifications({
        notifications: ['DISPATCH_AREA', 'AGENCY', 'SUBDIVISION', 'RADIO_CHANNEL', 'BEAT'],
    });
    const removeEventNotifications = useRemoveEventNotifications();
    const removeNotifications = useCallback(() => {
        removeEventNotifications({
            notifications: eventNotifications.map(
                (eventNotification) => eventNotification.notificationType
            ),
        });
    }, [eventNotifications, removeEventNotifications]);

    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const eventId = workspacePanel.linkedEntityId;

    return (
        <HStack spacing={0} alignItems="stretch">
            {eventNotifications.length > 0 ? (
                <EventNotificationsFieldIndicator onClick={removeNotifications} />
            ) : undefined}
            <FormEditable
                hasNotifications={eventNotifications.length > 0}
                viewMode={(containerProps) => (
                    <span {...containerProps}>
                        <AgencyFormGroupWithHeader
                            agency={agency}
                            hasNotifications={eventNotifications.length > 0}
                            showHeader={!eventId}
                        >
                            {viewMode}
                        </AgencyFormGroupWithHeader>
                    </span>
                )}
                editMode={({ toggleMode }) => (
                    <span onBlur={(e) => handleBlur(e, toggleMode)}>
                        <AgencyFormGroupWithHeader agency={agency} showHeader={!eventId}>
                            {editMode}
                        </AgencyFormGroupWithHeader>
                    </span>
                )}
            />
        </HStack>
    );
});
